// dependencies
import { Chart as ChartJS, LineElement, LinearScale, PointElement, TimeScale } from "chart.js";
import "chartjs-adapter-date-fns";
import { addDays, parseISO, startOfDay } from "date-fns";
import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";

// utils
import { getColor } from "../../../../../utils/getColor";

// config
import Config from "../../../Config";

ChartJS.register(TimeScale, LinearScale, LineElement, PointElement);

interface LineChartProps {
	fieldHistory: any;
	fieldTitle: string;
}

const getBackgroundGradient = (context) => {
	const backgroundColor = [
		`${Config.graphColor}99`,
		`${Config.graphColor}70`,
		`${Config.graphColor}60`,
		`${Config.graphColor}30`,
		`${Config.graphColor}10`,
	];

	if (!context?.chart?.chartArea || !context?.chart?.ctx || !context?.chart?.data?.datasets) {
		return;
	}

	const { ctx, chartArea } = context.chart;
	const { top, bottom } = chartArea;
	const gradient = ctx.createLinearGradient(0, top, 0, bottom);

	const colorTranch = 1 / (backgroundColor.length - 1);
	backgroundColor.forEach((color, index) => {
		gradient.addColorStop(0 + index * colorTranch, color);
	});
	return gradient;
};

const LineChart = (props: LineChartProps) => {
	const { fieldHistory, fieldTitle } = props;

	const firstDateInDataSet = fieldHistory.length ? parseISO(fieldHistory[0]?.asOf) : null;
	const lastDateInDataSet = fieldHistory.length ? parseISO(fieldHistory[fieldHistory.length - 1]?.asOf) : null;

	// used to determine the time unit for the x-axis
	const timeUnit = useMemo(() => {
		const secondsIn6Months = 60 * 60 * 24 * 30 * 6;

		const secondsBetweenDates =
			firstDateInDataSet && lastDateInDataSet
				? (lastDateInDataSet?.getTime() - firstDateInDataSet.getTime()) / 1000
				: 0;

		return secondsBetweenDates > secondsIn6Months
			? { unit: "month" as const, format: "MMM" }
			: { unit: "day" as const };
	}, [JSON.stringify(fieldHistory)]);

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		spanGaps: true,
		animations: {
			y: { duration: 0.2 },
		},
		scales: {
			y: {
				display: true,
				beginAtZero: true,
				ticks: {
					stepSize: 1,
					padding: 8,
					font: {
						size: 12,
						family: "Source Sans Pro, sans-serif",
					},
					beginAtZero: true,
					maxTicksLimit: 6,
				},
				grid: {
					drawTicks: false,
					color: getColor("neutral", 2),
					lineWidth: 1,
				},
				border: {
					color: getColor("neutral", 2),
				},
			},
			x: {
				type: "time" as const,
				suggestedMin: firstDateInDataSet ? startOfDay(firstDateInDataSet).toISOString() : undefined,
				suggestedMax: lastDateInDataSet ? addDays(startOfDay(lastDateInDataSet), 1).toISOString() : undefined,
				min: timeUnit.unit === "day" ? undefined : firstDateInDataSet?.toISOString(),
				max: timeUnit.unit === "day" ? undefined : lastDateInDataSet?.toISOString(),
				time: {
					unit: timeUnit.unit,
					displayFormats: {
						month: timeUnit?.format,
					},
				},
				ticks: {
					offset: true,
					padding: 8,
					font: {
						size: 12,
						family: "Source Sans Pro, sans-serif",
					},
					maxTicksLimit: timeUnit?.unit === "day" ? 10 : 12,
					major: {
						enabled: true,
					},
				},
				grid: {
					drawTicks: false,
					color: getColor("neutral", 2),
					lineWidth: 1,
				},
				border: {
					color: getColor("neutral", 2),
				},
			},
		},
		plugins: {
			tooltip: {
				displayColors: false,
				legend: {
					display: false,
				},
				callbacks: {
					label: (historyItem) => {
						return `${fieldTitle}: ${historyItem?.raw?.y || ""}`;
					},
				},
			},
		},
	};

	const data = {
		labels: fieldHistory.map((history) => history.asOf),
		datasets: [
			{
				label: "history",
				clip: 8,
				data: fieldHistory.map((history) => ({
					x: history.asOf,
					y: history.value,
				})),
				borderColor: Config.graphColor,
				pointBackgroundColor: "white",
				backgroundColor: (context) => getBackgroundGradient(context),
				borderWidth: 1.5,
				fill: true,
			},
		],
	};

	return (
		<div style={{ height: "264px" }} data-testid="field-history-chart">
			<Line data={data} options={options} />
		</div>
	);
};

export default LineChart;
